import {
  CheckFeatureAccessHeader,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { useQueryClient } from "@tanstack/react-query";
import { CamelCasedProperties } from "type-fest";
import { VITE_UTILITIES_BASE_URL } from "../../../env";
import { Utility } from "../../../types";
import { raise, recursiveCamelCaseCipher } from "../../postgrestApi";
import request from "../../request";
import { ClientOutput } from "../client";

const monthlyUsage = {
  useQuery,
};

export default monthlyUsage;

const QUERY_KEY = "backpack-sites-monthly-usage";

export type WeightedUsageSnakeCase = Omit<
  ClientOutput["utilities"]["NormalizedDataWrapper"]["normalized_data"][number],
  "utility_type" | "usage" | "cost" // Option<Decimal> in Rust are represented as strings in typegen but returned from service as numbers
> & { utility_type: Utility; cost?: number | null; usage?: number | null };

export type NormalizedUtilitySummary =
  CamelCasedProperties<WeightedUsageSnakeCase>;

// endpoint requires {feature: "Utilities", access: "read"}
const getMonthlyUsage = (
  siteId: number,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) =>
  request
    .get(`${VITE_UTILITIES_BASE_URL}/sites/${siteId}/utility_summary`)
    .set(checkFeatureAccessHeader)
    .then(
      (res) =>
        (recursiveCamelCaseCipher(res.body?.normalized_data) as Array<
          CamelCasedProperties<WeightedUsageSnakeCase>
        >) || raise("No data")
    );

function useQuery(siteId: number) {
  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    async (checkFeatureAccessHeader) =>
      getMonthlyUsage(siteId, checkFeatureAccessHeader),
    {
      queryKey: [QUERY_KEY, siteId],
      select: (data) => data.filter((month) => month.utilityType !== "waste"),
    }
  );
}

export const useInvalidateUtilitySummaryQuery = (siteId: number) => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY, siteId],
    });
};
